import { Button, toast } from '@heroui/react';
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { SelectMyImagePick } from '@/components/SelectMyImage';
import { DocRichTextEditor } from './components/DocRichTextEditor';
import {
  KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO,
  mergeMetaWithHeaderImage,
  normalizeMetaBackgroundForSave,
} from './components/knowledge-base-meta';
import type { KnowledgeBaseMetaBackground, KnowledgeBaseMetaHeaderImage } from './components/knowledge-base-meta';
import { trpc } from '@/lib/trpc';

const SAVE_DEBOUNCE_MS = 500;
const CREATE_TITLE = '未命名';

/**
 * 知识库文档路由页：`/knowledge-bases/doc/:docId`。拉取/保存；页面根即 {@link DocRichTextEditor}（顶栏 + 文档区单容器）。
 */
export function DocPage() {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const updateMutation = trpc.knowledgeBases.update.useMutation({
    onSuccess: async (_data, variables) => {
      await utils.knowledgeBases.listMy.invalidate();
      if (variables?.id) {
        await utils.knowledgeBases.get.invalidate({ id: variables.id });
      }
    },
  });

  const [editorMeta, setEditorMeta] = React.useState<unknown>({});

  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestContentRef = React.useRef<unknown>(undefined);
  const titleDraftRef = React.useRef<string>('');
  const effectiveIdRef = React.useRef<string | null>(null);
  const updateMutateRef = React.useRef(updateMutation.mutate);
  updateMutateRef.current = updateMutation.mutate;
  const updateMutateAsyncRef = React.useRef(updateMutation.mutateAsync);
  updateMutateAsyncRef.current = updateMutation.mutateAsync;
  const manualSavingRef = React.useRef(false);

  const [draftTitle, setDraftTitle] = React.useState('');

  const latestMetaRef = React.useRef<Record<string, unknown>>({});
  const pendingMetaBgRef = React.useRef<KnowledgeBaseMetaBackground | null>(null);
  const metaBgTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectHeaderImageOpen, setSelectHeaderImageOpen] = React.useState(false);

  React.useEffect(() => {
    if (!docId) {
      navigate('/knowledge-bases', { replace: true });
    }
  }, [docId, navigate]);

  const enabledGet = Boolean(docId);
  const docQuery = trpc.knowledgeBases.get.useQuery({ id: docId! }, { enabled: enabledGet });

  React.useEffect(() => {
    effectiveIdRef.current = docId ?? null;
  }, [docId]);

  const normalizeTitleForServer = React.useCallback((raw: string) => {
    const t = raw.trim();
    return t === '' ? CREATE_TITLE : t;
  }, []);

  const flushTitleSave = React.useCallback(() => {
    const id = effectiveIdRef.current;
    if (!id) return;
    updateMutateRef.current(
      { id, data: { title: normalizeTitleForServer(titleDraftRef.current) } },
      { onError: () => toast.danger('标题保存失败') },
    );
  }, [normalizeTitleForServer]);

  const flushContentSave = React.useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const id = effectiveIdRef.current;
    const content = latestContentRef.current;
    if (!id || content === undefined) return;
    updateMutateRef.current(
      { id, data: { content: content as Record<string, unknown> } },
      { onError: () => toast.danger('保存失败') },
    );
  }, []);

  const flushMetaBackgroundSave = React.useCallback(() => {
    if (metaBgTimerRef.current) {
      clearTimeout(metaBgTimerRef.current);
      metaBgTimerRef.current = null;
    }
    const id = effectiveIdRef.current;
    if (!id) return;
    const bg = pendingMetaBgRef.current;
    if (bg == null) return;
    pendingMetaBgRef.current = null;
    const normalized = normalizeMetaBackgroundForSave(bg);
    const nextMeta = { ...latestMetaRef.current, background: normalized };
    latestMetaRef.current = nextMeta;
    setEditorMeta(nextMeta);
    updateMutateRef.current(
      { id, data: { meta: nextMeta } },
      { onError: () => toast.danger('背景设置保存失败') },
    );
  }, []);

  const scheduleMetaBackgroundSave = React.useCallback(() => {
    const id = effectiveIdRef.current;
    if (!id) return;
    if (metaBgTimerRef.current) clearTimeout(metaBgTimerRef.current);
    metaBgTimerRef.current = setTimeout(() => {
      metaBgTimerRef.current = null;
      flushMetaBackgroundSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushMetaBackgroundSave]);

  const flushSave = React.useCallback(() => {
    flushTitleSave();
    flushContentSave();
    flushMetaBackgroundSave();
  }, [flushTitleSave, flushContentSave, flushMetaBackgroundSave]);

  const manualSaveWithToast = React.useCallback(async () => {
    if (manualSavingRef.current) return;
    manualSavingRef.current = true;
    try {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      if (metaBgTimerRef.current) {
        clearTimeout(metaBgTimerRef.current);
        metaBgTimerRef.current = null;
      }

      const id = effectiveIdRef.current;
      if (!id) {
        toast.danger('无效文档');
        return;
      }

      flushMetaBackgroundSave();

      const tasks: Array<Promise<unknown>> = [
        updateMutateAsyncRef.current({ id, data: { title: normalizeTitleForServer(titleDraftRef.current) } }),
      ];
      const content = latestContentRef.current;
      if (content !== undefined) {
        tasks.push(updateMutateAsyncRef.current({ id, data: { content: content as Record<string, unknown> } }));
      }

      await Promise.all(tasks);
      toast.success('保存成功');
    } catch {
      toast.danger('保存失败');
    } finally {
      manualSavingRef.current = false;
    }
  }, [normalizeTitleForServer, flushMetaBackgroundSave]);

  const scheduleSave = React.useCallback((content: unknown) => {
    latestContentRef.current = content;
    const id = effectiveIdRef.current;
    if (!id) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      const idInner = effectiveIdRef.current;
      const c = latestContentRef.current;
      if (!idInner || c === undefined) return;
      updateMutateRef.current(
        { id: idInner, data: { content: c as Record<string, unknown> } },
        { onError: () => toast.danger('保存失败') },
      );
    }, SAVE_DEBOUNCE_MS);
  }, []);

  const handleRichTextChange = React.useCallback(
    (value: unknown) => {
      scheduleSave(value);
    },
    [scheduleSave],
  );

  const handleLeave = React.useCallback(() => {
    flushSave();
    void utils.knowledgeBases.listMy.invalidate();
    navigate('/knowledge-bases');
  }, [flushSave, navigate, utils]);

  React.useEffect(() => {
    if (!docId) return;
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      if (metaBgTimerRef.current) {
        clearTimeout(metaBgTimerRef.current);
        metaBgTimerRef.current = null;
      }
    };
  }, [docId]);

  React.useEffect(() => {
    if (!docQuery.isSuccess || !docQuery.data) return;
    const m = docQuery.data.meta;
    if (m != null && typeof m === 'object' && !Array.isArray(m)) {
      latestMetaRef.current = { ...(m as Record<string, unknown>) };
      setEditorMeta(m);
    } else {
      latestMetaRef.current = {};
      setEditorMeta({});
    }
    pendingMetaBgRef.current = null;
    const t = docQuery.data.title ?? '';
    setDraftTitle(t);
    titleDraftRef.current = t;
  }, [docQuery.isSuccess, docQuery.data?.id]);

  const handleDocumentBackgroundChange = React.useCallback(
    (next: KnowledgeBaseMetaBackground) => {
      pendingMetaBgRef.current = next;
      const normalized = normalizeMetaBackgroundForSave(next);
      const nextMeta = { ...latestMetaRef.current, background: normalized };
      latestMetaRef.current = nextMeta;
      setEditorMeta(nextMeta);
      scheduleMetaBackgroundSave();
    },
    [scheduleMetaBackgroundSave],
  );

  const handleHeaderImagePick = React.useCallback((pick: SelectMyImagePick) => {
    const trimmed = pick.url.trim();
    if (!trimmed) return;
    const nextHeader: KnowledgeBaseMetaHeaderImage = {
      src: trimmed,
      cropNorm: pick.cropNorm,
      displayAspectRatio: KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO,
    };
    const id = effectiveIdRef.current;
    if (!id) return;
    const nextMeta = mergeMetaWithHeaderImage(latestMetaRef.current, nextHeader, KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO);
    latestMetaRef.current = nextMeta;
    setEditorMeta(nextMeta);
    updateMutateRef.current(
      { id, data: { meta: nextMeta, coverImage: null } },
      { onError: () => toast.danger('头图保存失败') },
    );
  }, []);

  const handleHeaderImageRemove = React.useCallback(() => {
    const id = effectiveIdRef.current;
    if (!id) return;
    const nextMeta = mergeMetaWithHeaderImage(latestMetaRef.current, null, KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO);
    latestMetaRef.current = nextMeta;
    setEditorMeta(nextMeta);
    updateMutateRef.current(
      { id, data: { meta: nextMeta, coverImage: null } },
      { onError: () => toast.danger('移除头图失败') },
    );
  }, []);

  const handleTitleInputBlur = React.useCallback(() => {
    flushTitleSave();
    const canonical = normalizeTitleForServer(titleDraftRef.current);
    setDraftTitle(canonical);
    titleDraftRef.current = canonical;
  }, [flushTitleSave, normalizeTitleForServer]);

  React.useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      flushSave();
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushSave]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSaveHotkey = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's';
      if (!isSaveHotkey) return;
      e.preventDefault();
      e.stopPropagation();
      void manualSaveWithToast();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [manualSaveWithToast]);

  const editInitialValue = React.useMemo(() => {
    if (!docQuery.data) return null;
    const c = docQuery.data.content;
    if (c == null || typeof c !== 'object' || Array.isArray(c)) return null;
    if (Object.keys(c as object).length === 0) return null;
    return c;
  }, [docQuery.data]);

  const handleTitleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setDraftTitle(v);
    titleDraftRef.current = v;
  }, []);

  if (!docId) return null;

  const showEditLoading = enabledGet && docQuery.isLoading;
  const showEditError = enabledGet && docQuery.isError;

  const editorKey = `edit-${docId}`;

  const coverImage = docQuery.data?.coverImage ?? null;

  return (
    <DocRichTextEditor
      mode="edit"
      title={draftTitle}
      meta={editorMeta}
      content={editInitialValue}
      coverImage={coverImage}
      shellClassName="h-full min-h-0 min-w-0 w-full flex-1 touch-pan-y overscroll-contain"
      editorKey={editorKey}
      onDocumentBackgroundChange={handleDocumentBackgroundChange}
      onTitleChange={handleTitleChange}
      onTitleBlur={handleTitleInputBlur}
      selectHeaderImageOpen={selectHeaderImageOpen}
      onSelectHeaderImageOpenChange={setSelectHeaderImageOpen}
      onHeaderImagePick={handleHeaderImagePick}
      onHeaderImageRemove={handleHeaderImageRemove}
      richTextPlaceholder={
        showEditLoading ? (
          <div className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground">
            加载文档…
          </div>
        ) : showEditError ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-sm text-destructive">
            <span>加载失败</span>
            <Button type="button" variant="secondary" size="sm" onPress={handleLeave}>
              返回列表
            </Button>
          </div>
        ) : undefined
      }
      onRichTextChange={handleRichTextChange}
      onRichTextBlur={flushContentSave}
    />
  );
}
